import React, { useEffect, useState } from "react";
import {
  FiPhone, FiX, FiClock, FiUser, FiFileText, FiMic, FiChevronLeft,
} from "react-icons/fi";
import type { CallLog, VoiceAgent } from "../voice.types";
import {
  getCallDetailDF,
  getCallAudioDF,
  type DfCallHistory,
} from "../../../services/voiceDataFabricService";
import { useCallDetailPanel } from "../../../context/CallDetailPanelContext";
import "../../../styles/Voice.css";

interface CallDetailApiResponse {
  first_name: string | null;
  last_name: string | null;
  phone_number: string | null;
  agent_id: string | null;
  agent_number: string | null;
  external_number: string | null;
  conversation_id: string | null;
  call_status: string | null;
  end_reason: string | null;
  call_successful: string | null;
  call_duration_secs: number | null;
  total_cost: number | null;
  transcript: string | null;
  transcript_summary: string | null;
  error_message: string | null;
}

function mapDfDetail(d: DfCallHistory): CallDetailApiResponse {
  const dur = d.callDurationSecs != null ? parseInt(d.callDurationSecs, 10) : null;
  return {
    first_name: d.firstName,
    last_name: d.lastName,
    phone_number: d.phoneNumber,
    agent_id: d.agentId,
    agent_number: d.agentNumber,
    external_number: d.externalNumber,
    conversation_id: d.conversationId,
    call_status: d.callStatus,
    end_reason: null,
    call_successful: null,
    call_duration_secs: dur != null && !isNaN(dur) ? dur : null,
    total_cost: null,
    transcript: d.transcript,
    transcript_summary: d.transcriptSummary,
    error_message: d.errorMessage,
  };
}

function formatSecs(s: number | null | undefined): string {
  if (!s) return "—";
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return m > 0 ? `${m}m ${rem}s` : `${rem}s`;
}

const StatusBadge: React.FC<{ value: string | null | undefined }> = ({ value }) => {
  if (!value) return <span className="call-detail__status-empty">—</span>;
  const v = value.toLowerCase().replace(/_/g, " ");
  const isGood = ["completed", "agent_hangup", "yes", "success"].some((k) =>
    value.toLowerCase().includes(k)
  );
  return (
    <span
      className="call-detail__status-badge"
      style={{
        background: isGood ? "rgba(52,211,153,0.1)" : "rgba(139,92,246,0.1)",
        color: isGood ? "var(--accent-green)" : "var(--accent-purple-light)",
        border: `1px solid ${isGood ? "rgba(52,211,153,0.25)" : "rgba(139,92,246,0.25)"}`,
      }}
    >
      {v}
    </span>
  );
};

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="call-detail__field">
    <span className="call-detail__field-label">{label}</span>
    <span className="call-detail__field-value">{children}</span>
  </div>
);

const CallDetailSidebarPanel: React.FC = () => {
  const { callDetailPanel, setCallDetailPanel } = useCallDetailPanel();
  const call = callDetailPanel?.call ?? null;
  const agents = callDetailPanel?.agents ?? [];

  const [detail, setDetail] = useState<CallDetailApiResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioLoading, setAudioLoading] = useState(false);
  const [audioError, setAudioError] = useState<string | null>(null);

  useEffect(() => {
    if (!call) return;
    setDetail(null);
    setError(null);
    setAudioUrl(null);
    setAudioError(null);
    if (!call.callSid) {
      setError("No call ID available for this record.");
      return;
    }
    setLoading(true);
    getCallDetailDF(call.callSid)
      .then((d) => { setDetail(mapDfDetail(d)); setLoading(false); })
      .catch((e) => { setError(e.message || "Failed to load call detail"); setLoading(false); });
  }, [call?.callSid]);

  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  if (!call) return null;

  const agentName =
    agents.find((a) => a.elevenLabsAgentId === call.agentId)?.name ??
    detail?.agent_id ?? call.agent;

  const dirColor =
    call.direction === "in" ? "var(--accent-green)"
    : call.direction === "missed" ? "var(--accent-red)"
    : "var(--accent-purple-light)";
  const dirLabel =
    call.direction === "in" ? "Inbound"
    : call.direction === "missed" ? "Missed"
    : "Outbound";

  const handleLoadAudio = async () => {
    if (!detail?.conversation_id) return;
    setAudioLoading(true);
    setAudioError(null);
    try {
      const url = await getCallAudioDF(detail.conversation_id);
      setAudioUrl(url);
    } catch (e: any) {
      setAudioError(e.message || "Recording unavailable");
    } finally {
      setAudioLoading(false);
    }
  };

  return (
    <div className="call-detail-panel">
      {/* Header */}
      <div className="call-detail-panel__header">
        <div className="call-detail__header-row">
          <div
            className="call-detail__icon-box"
            style={{ background: `${dirColor}1a`, border: `1px solid ${dirColor}40`, color: dirColor }}
          >
            <FiPhone size={16} />
          </div>
          <div>
            <div className="call-detail-panel__title">
              {call.contactName || call.to || call.from}
            </div>
            <div className="call-detail__meta-row">
              <span className="call-detail__direction-label" style={{ color: dirColor }}>
                {dirLabel}
              </span>
              <span className="call-detail__meta-timestamp">{call.timestamp}</span>
            </div>
          </div>
        </div>
        <button className="call-detail-panel__close" onClick={() => setCallDetailPanel(null)}>
          <FiChevronLeft size={16} />
        </button>
      </div>

      <div className="call-detail-panel__body">
        {loading && (
          <div className="call-detail__loading">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="voice-skeleton voice-skeleton--rect" style={{ borderRadius: 8 }} />
            ))}
          </div>
        )}

        {error && (
          <div className="call-detail__error-box">
            <FiX size={14} /> {error}
          </div>
        )}

        {detail && (
          <>
            {/* Stats row */}
            <div className="call-detail__stats-row">
              <div className="call-detail__stat-cell">
                <div className="call-detail__stat-value">{formatSecs(detail.call_duration_secs)}</div>
                <div className="call-detail__stat-label call-detail__stat-label--icon">
                  <FiClock size={10} /> Duration
                </div>
              </div>
              <div className="call-detail__stat-cell call-detail__stat-cell--bordered">
                <div className="call-detail__stat-value">
                  {detail.total_cost != null ? `${detail.total_cost.toFixed(4)}` : "—"}
                </div>
                <div className="call-detail__stat-label">Cost</div>
              </div>
              <div className="call-detail__stat-cell">
                <StatusBadge value={detail.call_successful === "yes" ? "Successful" : detail.call_successful} />
                <div className="call-detail__stat-label call-detail__stat-label--mt4">Result</div>
              </div>
            </div>

            {/* Fields */}
            <div className="call-detail__fields-grid">
              <Field label="Contact">
                <span className="call-detail__contact-row">
                  <FiUser size={11} className="call-detail__icon-muted" />
                  {[detail.first_name, detail.last_name].filter(Boolean).join(" ") || "—"}
                </span>
              </Field>
              <Field label="External Number">
                <span>{detail.external_number ?? detail.phone_number ?? "—"}</span>
              </Field>
              <Field label="Agent">{agentName ?? "—"}</Field>
              <Field label="Agent Line"><span>{detail.agent_number ?? "—"}</span></Field>
              <Field label="Status"><StatusBadge value={detail.call_status} /></Field>
              <Field label="End Reason"><StatusBadge value={detail.end_reason} /></Field>
            </div>

            {/* Recording */}
            {detail.conversation_id && (
              <div>
                <div className="call-detail__section-label"><FiMic size={11} /> Recording</div>
                {audioUrl ? (
                  <audio controls src={audioUrl} className="call-detail__audio" />
                ) : (
                  <div className="call-detail__recording-row">
                    <button className="glass-btn call-detail__load-audio-btn" onClick={handleLoadAudio} disabled={audioLoading}>
                      <FiMic size={12} /> {audioLoading ? "Loading…" : "Load Recording"}
                    </button>
                    {audioError && <span className="call-detail__audio-error">{audioError}</span>}
                  </div>
                )}
              </div>
            )}

            {/* Summary */}
            {detail.transcript_summary && (
              <div>
                <div className="call-detail__section-label"><FiFileText size={12} /> Summary</div>
                <div className="call-detail__summary-box">{detail.transcript_summary}</div>
              </div>
            )}

            {/* Transcript */}
            {detail.transcript && (
              <div>
                <div className="call-detail__section-label"><FiFileText size={12} /> Transcript</div>
                <div className="call-detail__transcript-box">{detail.transcript}</div>
              </div>
            )}

            {detail.error_message && (
              <div className="call-detail__error-box call-detail__error-box--start">
                <FiX size={13} className="call-detail__error-icon" />
                {detail.error_message}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CallDetailSidebarPanel;
